interface IconProps {
  size?: number;
  className?: string;
}

/**
 * Tab-bar icons — ported 1:1 from the legacy `IconComponents` (Material Symbols
 * paths, viewBox "0 -960 960 960"). Color is inherited via `currentColor`.
 */

const base = (size: number, className?: string) => ({
  width: size,
  height: size,
  viewBox: '0 -960 960 960',
  fill: 'currentColor',
  className,
  'aria-hidden': true as const,
});

export const HeadphonesIcon = ({ size = 22, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M360-120H200q-33 0-56.5-23.5T120-200v-280q0-75 28.5-140.5t77-114q48.5-48.5 114-77T480-840q75 0 140.5 28.5t114 77q48.5 48.5 77 114T840-480v280q0 33-23.5 56.5T760-120H600v-320h160v-40q0-117-81.5-198.5T480-760q-117 0-198.5 81.5T200-480v40h160v320Zm-80-240h-80v160h80v-160Zm400 0v160h80v-160h-80Zm-400 0h-80 80Zm400 0h80-80Z" />
  </svg>
);

export const RocketIcon = ({ size = 22, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="m226-559 78 33q14-28 29-54t33-52l-56-11-84 84Zm142 83 114 113q42-16 90-49t90-75q70-70 109.5-155.5T806-800q-72-5-158 34.5T492-656q-42 42-75 90t-49 90Zm178-65q-23-23-23-56.5t23-56.5q23-23 57-23t57 23q23 23 23 56.5T660-541q-23 23-57 23t-57-23Zm19 321 84-84-11-56q-26 18-52 32.5T532-299l33 79Zm313-653q19 121-23.5 235.5T708-419l20 99q4 20-2 39t-20 33L538-80l-84-197-171-171-197-84 167-168q14-14 33.5-20t39.5-2l99 20q104-104 218-147t235-24ZM157-321q35-35 85.5-35.5T328-322q35 35 34.5 85.5T327-151q-25 25-83.5 43T82-76q14-103 32-161.5t43-83.5Zm57 56q-10 10-20 36.5T180-175q27-4 53.5-13.5T270-208q12-12 13-29t-11-29q-12-12-29-11.5T214-265Z" />
  </svg>
);

export const CardsStarIcon = ({ size = 22, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="m489-460 91-55 91 55-24-104 80-69-105-9-42-98-42 98-105 9 80 69-24 104Zm19 260h224q-7 26-24 42t-44 20L228-85q-33 5-59.5-15.5T138-154L85-591q-4-33 16-59t53-30l46-6v80l-36 5 54 437 290-36Zm-148-80q-33 0-56.5-23.5T280-360v-440q0-33 23.5-56.5T360-880h440q33 0 56.5 23.5T880-800v440q0 33-23.5 56.5T800-280H360Zm0-80h440v-440H360v440Zm220-220ZM218-164Z" />
  </svg>
);

export const AccountCircleIcon = ({ size = 22, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M234-276q51-39 114-61.5T480-360q69 0 132 22.5T726-276q35-41 54.5-93T800-480q0-133-93.5-226.5T480-800q-133 0-226.5 93.5T160-480q0 59 19.5 111t54.5 93Zm246-164q-59 0-99.5-40.5T340-580q0-59 40.5-99.5T480-720q59 0 99.5 40.5T620-580q0 59-40.5 99.5T480-440Zm0 360q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q53 0 100-15.5t86-44.5q-39-29-86-44.5T480-280q-53 0-100 15.5T294-220q39 29 86 44.5T480-160Zm0-360q26 0 43-17t17-43q0-26-17-43t-43-17q-26 0-43 17t-17 43q0 26 17 43t43 17Zm0-60Zm0 360Z" />
  </svg>
);

/**
 * ios-tab-bar — filled variants for the iOS bar (QA PO 2026-10-30), Material
 * Symbols weight 400 `-fill`, the same family as the outlined set and the 🎵.
 */

export const HeadphonesFillIcon = ({ size = 22, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M328-120H180q-24 0-42-18t-18-42v-300q0-75 28.5-140.5t77-114q48.5-48.5 114-77T480-840q75 0 140.5 28.5t114 77q48.5 48.5 77 114T840-480v300q0 24-18 42t-42 18H632v-296h148v-64q0-125-87.5-212.5T480-780q-125 0-212.5 87.5T180-480v64h148v296Z" />
  </svg>
);

export const RocketFillIcon = ({ size = 22, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="m98-537 168-168q14-14 33-20t39-2l77 14q-55 62-89 117.5T263-466L98-537Zm202 89q27-73 68-137.5T461-702q88-88 201-131.5T873-860q17 98-26 211T716-448q-52 52-117 93t-138 68L300-448Zm335.5-105q29.5 0 49.5-20t20-49.5q0-29.5-20-49.5t-49.5-20q-29.5 0-49.5 20t-20 49.5q0 29.5 20 49.5t49.5 20ZM551-85l-72-165q74-29 129.5-63T726-402l14 77q4 20-2 39.5T718-252L551-85ZM162-318q35-35 85-35.5t85 34.5q35 35 35 85t-35 85q-26 26-81 43T87-74q15-109 32-163.5t43-80.5Z" />
  </svg>
);

export const CardsStarFillIcon = ({ size = 22, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="m478-449 91-55 91 55-24-104 80-69-105-9-42-98-42 98-105 9 80 69-24 104ZM213-81q-25 3-44.5-11.5T146-132L82-630q-3-25 12-45t40-23l64-7v387q0 50 34.5 85t84.5 35h489q0 20-12 34.5T762-147L213-81Zm105-177q-24 0-42-18t-18-42v-502q0-24 18-42t42-18h502q24 0 42 18t18 42v502q0 24-18 42t-42 18H318Z" />
  </svg>
);

export const AccountCircleFillIcon = ({ size = 22, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M222-255q63-40 124.5-60.5T480-336q72 0 134 20.5T739-255q44-54 62.5-109T820-480q0-145-97.5-242.5T480-820q-145 0-242.5 97.5T140-480q0 61 19 116t63 109Zm160.5-234.5Q343-529 343-587t39.5-97.5Q422-724 480-724t97.5 39.5Q617-645 617-587t-39.5 97.5Q538-450 480-450t-97.5-39.5ZM480-80q-84 0-157-31.5t-127-86Q142-252 111-325T80-480q0-83 31.5-156t86-127Q252-817 325-848.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 82-31.5 155T763-197.5q-54 54.5-127 86T480-80Z" />
  </svg>
);

/**
 * fab-music-icon — the centre button's note, Material Symbols "music_note"
 * (weight 400), so it belongs to the same family as the four tabs. The glyph is
 * centred geometrically, but its mass is the note head, low and on the left:
 * nudged 1px right and 1px up so the eye reads it as centred in the round
 * button. A tuning value, validated on an iPhone.
 */
export const MusicNoteIcon = ({ size = 22, className }: IconProps) => (
  <svg {...base(size, className)} style={{ transform: 'translate(1px, -1px)' }}>
    <path d="M400-120q-66 0-113-47t-47-113q0-66 47-113t113-47q23 0 42.5 5.5T480-418v-422h240v160H560v400q0 66-47 113t-113 47Z" />
  </svg>
);
